import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = '/api';
const BRAND = '#FF385C';

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const Field = ({ label, value, name, editing, onChange, type = 'text', textarea }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {label}
    </label>
    {editing ? (
      textarea ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(name, e.target.value)}
          rows={3}
          style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem', resize: 'vertical' }}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(name, e.target.value)}
          style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem' }}
        />
      )
    ) : (
      <div style={{ fontSize: '0.9rem', color: '#111827' }}>{value || '—'}</div>
    )}
  </div>
);

const AdminStoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [editData, setEditData] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/admin/stores/${id}`, { headers: headers() })
      .then((r) => {
        if (!r.ok) throw new Error('Store not found');
        return r.json();
      })
      .then((data) => {
        setStore(data);
        setEditData({
          name: data.name,
          description: data.description,
          city: data.city,
          url_name: data.url_name,
          website: data.website,
          contact_email: data.contact_email,
          phone_number: data.phone_number,
          address: data.address,
          business_type: data.business_type,
        });
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  const handleFieldChange = (name, value) => {
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/stores/${id}`, {
        method: 'PATCH',
        headers: headers(),
        body: JSON.stringify(editData),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Update failed');
      }
      const updated = await res.json();
      setStore((prev) => ({ ...prev, ...updated }));
      setEditing(false);
      setSuccess('Store updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/admin/stores/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) throw new Error('Delete failed');
      navigate('/admin/stores');
    } catch (err) {
      setError(err.message);
      setDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <div className="spinner-border" role="status" style={{ color: BRAND }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !store) {
    return (
      <div>
        <button onClick={() => navigate('/admin/stores')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', marginBottom: '1rem' }}>
          <i className="bi bi-arrow-left me-1" /> Back to Stores
        </button>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '1rem', color: '#991b1b' }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <button onClick={() => navigate('/admin/stores')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.85rem', marginBottom: 4 }}>
            <i className="bi bi-arrow-left me-1" /> Back to Stores
          </button>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>{store.name}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: BRAND, color: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>
                <i className="bi bi-pencil me-1" /> Edit
              </button>
              <button onClick={() => window.open(`/${store.city.toLowerCase()}/${store.url_name}`, '_blank')} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>
                <i className="bi bi-box-arrow-up-right me-1" /> View Live
              </button>
              <button onClick={() => setDeleteModal(true)} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#ef4444', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>
                <i className="bi bi-trash me-1" /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {success && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '0.75rem 1rem', color: '#065f46', marginBottom: '1rem' }}>{success}</div>
      )}
      {error && store && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '0.75rem 1rem', color: '#991b1b', marginBottom: '1rem' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Store info card */}
        <div style={{ flex: '1 1 380px', background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1.25rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem' }}>Store Details</h3>
          <Field label="Name" value={editing ? editData.name : store.name} name="name" editing={editing} onChange={handleFieldChange} />
          <Field label="Description" value={editing ? editData.description : store.description} name="description" editing={editing} onChange={handleFieldChange} textarea />
          <Field label="City" value={editing ? editData.city : store.city} name="city" editing={editing} onChange={handleFieldChange} />
          <Field label="URL Name" value={editing ? editData.url_name : store.url_name} name="url_name" editing={editing} onChange={handleFieldChange} />
          <Field label="Business Type" value={editing ? editData.business_type : store.business_type} name="business_type" editing={editing} onChange={handleFieldChange} />
          <Field label="Website" value={editing ? editData.website : store.website} name="website" editing={editing} onChange={handleFieldChange} />
          <Field label="Phone" value={editing ? editData.phone_number : store.phone_number} name="phone_number" editing={editing} onChange={handleFieldChange} />
          <Field label="Address" value={editing ? editData.address : store.address} name="address" editing={editing} onChange={handleFieldChange} />

          <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #f3f4f6' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>Owner</div>
            <div style={{ fontSize: '0.9rem', color: '#111827' }}>{store.owner_email || '—'}</div>
            {store.owner_name && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{store.owner_name}</div>}
          </div>

          {store.categories && store.categories.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>Categories</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {store.categories.map((c) => (
                  <span key={c.id} style={{ background: '#f3f4f6', color: '#374151', padding: '2px 10px', borderRadius: 20, fontSize: '0.8rem' }}>{c.name}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#9ca3af' }}>
            Created {new Date(store.created_at).toLocaleDateString()}
          </div>
        </div>

        {/* Products card */}
        <div style={{ flex: '1 1 380px', background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '1.25rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem' }}>
            Products ({store.products ? store.products.length : 0})
          </h3>
          {(!store.products || store.products.length === 0) ? (
            <div style={{ color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>No products yet</div>
          ) : (
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              {store.products.map((p) => (
                <div key={p.id} style={{
                  display: 'flex', gap: '0.75rem', padding: '0.75rem 0',
                  borderBottom: '1px solid #f3f4f6', alignItems: 'center',
                }}>
                  {p.image_url ? (
                    <img
                      src={p.image_url.startsWith('/server/uploads') ? p.image_url : `/api${p.image_url}`}
                      alt={p.name}
                      style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="bi bi-image" style={{ color: '#9ca3af' }} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      ${parseFloat(p.price).toFixed(2)} &middot; {p.item_type || 'product'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete modal */}
      {deleteModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '2rem', maxWidth: 420, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>Delete Store?</h3>
            <p style={{ color: '#4b5563', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              This will permanently delete <strong>{store.name}</strong> and all its products. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteModal(false)} disabled={deleting} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 500, opacity: deleting ? 0.6 : 1 }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStoreDetail;
